import { Inject } from '@odinjs/odin';
import { CardAppointmentsAPI } from '@philips-emr/patient-components/src';
import { AutocompleteAPI, ButtonAPI, DeckAPI, FetchAPI, FilterAPI, ModalService, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount, OnSubmit, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableCloseEvent, ClickEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BrokerService, HistoryService, HttpService, MediaType, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import React, { ReactElement } from 'react';
import { AppointmentsListAliases, FragmentNames } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { APPOINTMENT_TYPE } from '../../constants/appointment-type';
import { ScheduleType } from '../../constants/schedule-type';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';
import { AppointmentUtils } from '../../utils/appointment-utils';
import { defaultIfUndefined } from '../../utils/object-utils';
import RescheduleContext, { rescheduleCancelModal } from '../../utils/reschedule-utils';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import { UrlTelemedicine } from '../home/patient-root-controller';
import PseudonymCancelService from '../pseudonym-appointments/pseudonym-service/pseudonym-cancel-service';
import './appointments-list-controller.css';
import { RescheduleCheckup } from '../../interfaces/reschedule-checkup';

let defaultDisplayRules: Record<string, Value>[] = [];
let defaultdisplaySpecialitys: Record<string, Value>[] = [];

interface FilterParams {
  [key: string]: string | number | string[];
}

interface PhysicioanRecord {
  cdPessoaFisica: string;
  nmPessoaFisica: string;
}

interface SpecialityRecord {
  cdSpeciality: number;
  dsSpeciality: string;
}

interface ExamRecord {
  cdExam: number;
  dsExam: string;
}

interface FilterList {
  cancelled: string;
  categories: FilterItem[];
  exam: ExamRecord;
  initialDate: string;
  physicians: PhysicioanRecord;
  speciality: SpecialityRecord;
}

interface FilterItem {
  label: string;
  value: string;
}

@Controller({ component: AppointmentsListAliases.LAYOUT_APPOINTMENTS, fragment: FragmentNames.APPOINTMENTS_LIST })
export default class AppointmentsListController extends BaseController {

  @Inject('button-add-appointment')
    btnCriar!: ButtonAPI;

  @Inject(AppointmentsListAliases.APPOINTMENTS_FETCH)
    appointmentsFetchApi!: FetchAPI;

  @Inject('deck-appointments')
    deckAppointments!: DeckAPI;

  @Inject('deck-waiting-list')
    deckWaitingList!: DeckAPI;

  @Inject('panel-deck-waiting-list')
    panelDeckWaitingListApi!: PanelAPI;

  @Inject('fetch-waiting-list')
    fetchWaitingListApi!: FetchAPI;

  @Inject('autocomplete-categories')
    autocompleteCategoriesApi!: AutocompleteAPI;

  @Inject('filter-appointments-list')
    filterAppointmentsListApi!: FilterAPI;

  @Inject('PseudonymCancelService')
    pseudonymCancelService!: PseudonymCancelService;

  @Inject(AppointmentsListAliases.AUTOCOMPLETE_SPECIALITYS)
    autocompleteSpecialityApi!: AutocompleteAPI;

  @OnAfterFetch({target: 'fetch-waiting-list'})
  handleFetchWaitingListLoading(event: FetchEvent): void {
    const data = event.data as [];
    if(data.length === 0) {
      this.panelDeckWaitingListApi.setVisible(false);
    }
  }

  @OnMount(AppointmentsListAliases.LAYOUT_APPOINTMENTS)
  onMountLayoutAppointments(): void {

    /** TO-DO Remove when jackson properly quote strings in JSON format */
    const options = {
      headers: {
        accept: MediaType.TEXT_PLAIN,
      }
    };
    HttpService.get(APPLICATION_ENDPOINTS.NEW_SCHEDULING_ALLOWED, {}, options).then(response => {
      if (response.body === 'N') {
        this.btnCriar.setEnabled(false);
      }
    });

  }

  @OnUnmount(AppointmentsListAliases.LAYOUT_APPOINTMENTS)
  handleUnMountLayoutAppointments(): void {
    ToastService.closeAll();
  }

  @OnClick({ target: ['button-add-appointment'] })
  onClickNewAppointment(): void {
    const { patientId } = UserContext;
    applicationContext.redirect(APPLICATION_ROUTES.PSEUDONYM_APPOINTMENT_FORM, { patientId });
  }

  setCardContext (appointmentType: APPOINTMENT_TYPE, event: ClickEvent): void {
    const { nrSequencia, ieScheduleType } = event.value;
    const { patientId } = UserContext;
    const params = {
      appointmentType,
      ieScheduleType,
      nrSeq: nrSequencia,
      patientId: String(patientId),
    };
    applicationContext.redirect(APPLICATION_ROUTES.PSEUDONYM_APPOINTMENT_DETAIL, params);
  }

  @OnClick({ target: ['deck-appointments'] })
  onCardDeckClick(event: ClickEvent): void {
    if (event?.value != null) {
      const { ieScheduleType } = event.value;

      switch(ieScheduleType) {
        case ScheduleType.CHECKUP: this.setCardContext(APPOINTMENT_TYPE.CHECKUP, event); break;
        case ScheduleType.CONSULTATION: this.setCardContext(APPOINTMENT_TYPE.CONSULTATIONS, event); break;
        case ScheduleType.EXAM: this.setCardContext(APPOINTMENT_TYPE.EXAMS, event); break;
        case ScheduleType.SERVICE: this.setCardContext(APPOINTMENT_TYPE.SERVICES, event); break;
        case ScheduleType.SURGERY: this.setCardContext(APPOINTMENT_TYPE.SURGERIES, event); break;
        case ScheduleType.CHEMOTHERAPY: this.setCardContext(APPOINTMENT_TYPE.CHEMOTHERAPIES, event); break;
        case ScheduleType.REQUEST: this.setCardContext(APPOINTMENT_TYPE.REQUEST, event); break;
      }
    }
  }

  @OnAfterFetch(AppointmentsListAliases.APPOINTMENTS_FETCH)
  onAfterFetch(event: FetchEvent): void {
    event.data = {...event.data};
  }

  @OnDynamicContentRender({ target: 'deck-appointments' })
  onDeckGroupRender(event: RenderEvent): void {

    if (event) {
      const { render } = event;
      const eventData = event.data as Record<string, Value>[];
      const firstData = eventData[0];
      const date = moment(`${event.entry}:${firstData.hrAgendamento}`, 'YYYY-MM-DD:HH:mm');
      const dateFormat = date.format('DD MMMM');
      const today = moment.utc().startOf('day');
      const dif = today.diff(date, 'days') ;
      const indicateDay = dif !== 0 ? date.fromNow() : date.calendar().split(' ')[0];
      const renderSuggestionDate: () => ReactElement = () => {
        return (
          <div className='deck-title-appointments-list-controller'  >
            <span>{`${dateFormat} (${indicateDay})`} </span>
          </div>
        );
      }
      render(renderSuggestionDate);
    }
  }

  @OnDynamicContentRender(AppointmentsListAliases.DECK_WAITING_LIST)
  onWaitingListDeckGroupRender(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const renderSuggestionDate: () => ReactElement = () => {
        return (
          <div className='deck-title-appointments-list-controller'  >
            <span>{MessageService.translate('patient.label.modal-scheduling-waiting-list')}</span>
          </div>
        );
      }
      render(renderSuggestionDate);
    }
  }

  @OnClick({ target: 'deck-waiting-list' })
  handleClickWaitingListCard(event: ClickEvent): void {
    const { nrSequenceWaitingList } = event.value;
    applicationContext.redirect(APPLICATION_ROUTES.PSEUDONYM_APPOINTMENT_DETAIL, {
      appointmentType: APPOINTMENT_TYPE.WAITING_LIST,
      nrSeq: nrSequenceWaitingList ,
      patientId: UserContext.patientId
    });
  }

  @OnMount({ target: 'autocomplete-categories' })
  onMountAutocomplete(): void {
    this.autocompleteCategoriesApi.setData(defaultDisplayRules);
  }

  @OnMount(AppointmentsListAliases.AUTOCOMPLETE_SPECIALITYS)
  onMountAutocompleteSpeciality(): void {
    this.autocompleteSpecialityApi.setData(defaultdisplaySpecialitys);
  }

  setParamFilter(params: FilterParams, value: string | number | string[], field: string): void {
    if (value != null) {
      params[field] = value;
    }
  }

  @OnMount(AppointmentsListAliases.FILTER_APPOINTMENTS_LIST)
  onMountFilter(): void {
    AppointmentUtils.getDisplayConfig().then(displayRules => {
      defaultDisplayRules = displayRules;
      this.context.set({ filterAppointmentsLoaded: true })
    });
    AppointmentUtils.getDisplaySpecialitys().then(displaySpecialitys => {
      defaultdisplaySpecialitys = displaySpecialitys;
      const record = {
        cancelled: true,
      };
      this.filterAppointmentsListApi.setData(record);
      this.filterAppointmentsListApi.submit();
    });
  }

  @OnSubmit(AppointmentsListAliases.FILTER_APPOINTMENTS_LIST)
  onSubmitFilter(event: SubmitEvent): void {
    const record = event.record as FilterList;
    const isFilterAppointmentsLoaded = this.context.get('filterAppointmentsLoaded');
    const params = {};

    if (isFilterAppointmentsLoaded) {
      this.setParamFilter(params, record.categories?.map(item => item.value), 'filter');
      this.setParamFilter(params, record.initialDate, 'initialDate');
      this.setParamFilter(params, record.physicians?.cdPessoaFisica, 'physician');
      this.setParamFilter(params, record.speciality?.cdSpeciality, 'speciality');
      this.setParamFilter(params, record.exam?.cdExam, 'exam');
      this.setParamFilter(params, record.cancelled ? 'S' : 'N', 'cancelled');
      this.appointmentsFetchApi.setParameters(params);
    }
  }

  @OnMount(AppointmentsListAliases.CARD_APPOINTMENTS_DECK_APPOINTMENTS_APPOINTMENTS_LIST)
  handleCardAppointmentsListLoading(event: MountEvent): void {
    const cardAppointment = event.target as CardAppointmentsAPI;
    const patientId = UserContext.patientId;

    cardAppointment.setOnEnterTeleconsultation(this.onEnterTeleconsultation);
    cardAppointment.setOnAppointmentRescheduleMore(this.handleAppointmentRescheduleMore);
    cardAppointment.setOnAppointmentReschedule(this.handleAppointmentReschedule);
    cardAppointment.setOnAppointmentCancel(this.handleAppointmentCancel);
    cardAppointment.setOnAppointmentConfirm(this.handleAppointmentConfirm);
    cardAppointment.setOnPayment(this.handlePayment);
    cardAppointment.setPatientId(patientId);

    const rescheduleParameters = RescheduleContext.rescheduleParameters;
    cardAppointment.setRescheduleConsultation(rescheduleParameters.rescheduleCheckup, rescheduleParameters.reagendarConsulta, rescheduleParameters.reagendarExame, rescheduleParameters.reagendarServico);

    const scheduleParameters = RescheduleContext.scheduleParameters;
    cardAppointment.setCancelSchedules(scheduleParameters.allowCancelCheckup);
    cardAppointment.setConfirmSchedules(scheduleParameters.allowConfirmCheckup);
  }
  private handleAppointmentRescheduleMore = (): void => {
    const { patientId } = UserContext;

    ModalService.openFullscreen({
      closeable: true,
      contextData: { patientId },
      fragment: FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL,
      id: FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL,
      onBeforeClose: (event) => this.handleBeforeCloseRescheduleModal(event),
      onClose: () => { return },
      title: 'reschedule.title',
    });
  }

  private handleBeforeCloseRescheduleModal(event: AbortableCloseEvent): void {
    event.abort();

    if (RescheduleContext.hasInsuranceSelected) {
      ModalService.openDialog(rescheduleCancelModal);
    } else {
      ModalService.close(FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL);
      RescheduleContext.hasInsuranceSelected = false;
    }
  }

  private handleAppointmentReschedule = (sequenceSchedule: number, scheduleType: string): void => {
    if (ScheduleType.CHECKUP === scheduleType) {

      HttpService.get(APPLICATION_ENDPOINTS.GET_CHECKUP_DETAIL, {checkupSequence: sequenceSchedule})
        .then(response => {
          RescheduleContext.rescheduleCheckup = response.body as RescheduleCheckup;
          this.historyService.push(APPLICATION_ROUTES.RESCHEDULE_CHECKUP);
        });
    } else {
      const listItem = `${sequenceSchedule},`;
      const body = (scheduleType === ScheduleType.CONSULTATION || scheduleType === ScheduleType.SERVICE)
        ? { listaConsultaP: listItem, listaExameP: '' }
        : { listaConsultaP: '', listaExameP: listItem };

      HttpService.post(
        APPLICATION_ENDPOINTS.RESCHEDULE_ITEMS_TRANSFERENCE, { patientId: UserContext.patientId }, { body }
      ).then(response => {
        RescheduleContext.nrSeqAgeint = response.body as number;
        HistoryService.push(APPLICATION_ROUTES.RESCHEDULE_APPOINTMENTS);
      });
    }
  }

  private handleAppointmentCancel = (patientId: number, scheduleType: string, status: string, sequenceSchedule: number): void => {
    const cancelSubmitAction = (): void => { this.appointmentsFetchApi.forceFetch() };
    const cancelSubmitWaitingListAction = (): void => { BrokerService.publishToTopic('updateDashboardCard', { patientId }) };
    scheduleType === ScheduleType.WAITING_LIST
      ? this.pseudonymCancelService.cancelWaitingList(patientId, sequenceSchedule, cancelSubmitWaitingListAction)
      : this.pseudonymCancelService.cancelItem(patientId, sequenceSchedule, scheduleType, status, cancelSubmitAction);
  }

  private handleAppointmentConfirm = (patientId: number, scheduleType: string, scheduleCode: number, sequenceSchedule: number): void => {
    const confirmSubmitAction = (): void => { this.appointmentsFetchApi.forceFetch() };
    this.pseudonymCancelService.confirmItem(patientId, scheduleCode, sequenceSchedule, scheduleType, confirmSubmitAction);
  }

  private handlePayment = (nrSeqAgendaInt: number): void => {
    AppointmentUtils.mountModalPayment(nrSeqAgendaInt);
  }

  @OnMount(AppointmentsListAliases.CARD_APPOINTMENTS_WAITING_LIST)
  handleCardAppointmentsWaitingListLoading(event: MountEvent): void {
    const cardAppointment = event.target as CardAppointmentsAPI;
    cardAppointment.setFunctionToCancelWaitList(this.handleAppointmentCancelWaitingList);
  }

  private handleAppointmentCancelWaitingList = (sequenceSchedule: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        const cancelWaitingListAction = (): void => {
          this.fetchWaitingListApi.forceFetch();
          showToastNotificationSuccess(MessageService.translate('patient.toast.waiting-list-item-removed'));
        };
        this.pseudonymCancelService.cancelWaitingList(UserContext.patientId, sequenceSchedule, cancelWaitingListAction);

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  private onEnterTeleconsultation = (nrSeqAgendaConsulta: number, ieScheduleType: string): void => {
    const patientId = UserContext.patientId;
    PatientHttpService.get(APPLICATION_ENDPOINTS.GET_URL_TELEMEDICINE, {
      ieScheduleType,
      nrSeqAgendaConsulta,
      patientId
    }).then(response=>{
      const urlTelemedicina = response.body as UrlTelemedicine;
      BrokerService.publishToTopic('iframe-teleconsulta', {patientUrl: urlTelemedicina.patientUrl});
    }).catch(({ response }: HttpServiceError<string>) => {
      const message = defaultIfUndefined(response?.body, 'newaccount.error.internal.server.error.try.again');
      showToastNotificationError(message);
    });
  }

}
